import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { Region } from '../../utils';
import {
  type AuthenticationMode,
  AuthenticationProvider as AuthenticationProviderAuth0,
  useAuth as useAuthAuth0,
  useSignupAuth as useSignupAuthAuth0,
} from './auth0/auth';
import { UseAuthContextType } from './auth0/auth.types';
import { getDefaultUseAuthState } from './auth0/helpers';
import {
  AuthenticationProvider as AuthenticationProviderOverride,
  useAuth as useAuthOverride,
} from './override/auth';
import { store as overrideStore } from './override/store';

type AuthFactoryContextState = {
  useAuth: () => UseAuthContextType;
  overrideToken: string | undefined;
  setOverrideToken: (token: string | undefined) => void;
};

const DefaultUseAuthState = getDefaultUseAuthState();
const AuthFactoryDefaultState = {
  useAuth: () => DefaultUseAuthState,
  overrideToken: undefined,
  setOverrideToken: () => {},
};

export const AuthFactoryContext = createContext<AuthFactoryContextState>(
  AuthFactoryDefaultState,
);

export const AuthFactoryProvider = ({
  children,
  mode = 'application',
  ...providerProps
}: {
  children: ReactElement;
  clientId: string;
  redirectUri?: string;
  mode?: AuthenticationMode;
  onTokenReceived: (token: string, region: Region) => void;
}) => {
  const [overrideToken, setOverrideTokenState] = useState<
    AuthFactoryContextState['overrideToken']
  >(overrideStore.get());

  const setOverrideToken = useCallback((token?: string) => {
    if (token) {
      overrideStore.set(token);
    } else {
      overrideStore.remove();
    }

    setOverrideTokenState(token);
  }, []);

  const { AuthenticationProvider: CurrentAuthenticationProvider, ...state } =
    useMemo(() => {
      const sharedState = {
        overrideToken,
        setOverrideToken,
      };

      // manual override (also used for test env)
      if (overrideToken) {
        return {
          ...sharedState,
          AuthenticationProvider: AuthenticationProviderOverride,
          useAuth: useAuthOverride,
        };
      }

      // default to auth0
      return {
        ...sharedState,
        AuthenticationProvider: AuthenticationProviderAuth0,
        useAuth: mode === 'signup' ? useSignupAuthAuth0 : useAuthAuth0,
      };
    }, [mode, overrideToken, setOverrideToken]);

  return (
    <AuthFactoryContext.Provider value={state}>
      <CurrentAuthenticationProvider {...providerProps} mode={mode}>
        {children}
      </CurrentAuthenticationProvider>
    </AuthFactoryContext.Provider>
  );
};

export const useAuthFactory = () => useContext(AuthFactoryContext);

export const useAuth = () => {
  const { useAuth: currentUseAuth } = useAuthFactory();

  return currentUseAuth();
};
